import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, Button } from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Save,
  Store,
  Warehouse,
  Truck,
  Trash2,
  MapPin,
  Clock,
  DollarSign,
  Info,
  Package,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  listenToUserCommercePartners,
  upsertCommercePartner,
  setCurrentPartner,
} from "../../redux/commercePartnersSlice";
import { RootState } from "../../redux/store";
import DefaultLogo from "../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════
   ARMERIX DESIGN TOKENS
═══════════════════════════════════════════════════════ */
const T = {
  bg:       "#FFFFFF",
  bgSoft:   "#F8F5F0",
  bgMuted:  "#EDE8E0",
  ink:      "#0D0D0D",
  muted:    "#8A7A6A",
  mutedSoft:"#C4B8A8",
  border:   "rgba(184,134,11,0.18)",
  gold:     "#B8860B",
  red:      "#CC2200",
  orange:   "#FF6B00",
  green:    "#15803D",
  gradH:    "linear-gradient(90deg,#0D0D0D 0%,#B8860B 25%,#CC2200 60%,#FF6B00 100%)",
  gradA:    "linear-gradient(90deg,#0D0D0D 0%,#B8860B 20%,#CC2200 55%,#FF6B00 80%,#B8860B 100%)",
  grad135:  "linear-gradient(135deg,#0D0D0D 0%,#B8860B 35%,#CC2200 65%,#FF6B00 100%)",
  shadow:   "0 14px 40px rgba(0,0,0,0.10)",
  shadowGold:"0 8px 28px rgba(184,134,11,0.28)",
  shadowRed: "0 8px 28px rgba(204,34,0,0.30)",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes slideDown{from{opacity:0;transform:translateY(-8px);}to{opacity:1;transform:translateY(0);}}
  @keyframes pulse{0%,100%{opacity:1;}50%{opacity:.6;}}

  .arm-card{background:#fff;border:1px solid rgba(184,134,11,0.18);border-radius:6px;overflow:hidden;transition:box-shadow .2s,transform .2s;}
  .arm-card:hover{box-shadow:0 14px 40px rgba(0,0,0,0.10);}

  .arm-zone-header{display:flex;align-items:center;gap:12px;padding:16px 18px;cursor:pointer;user-select:none;transition:background .14s;background:#fff;}
  .arm-zone-header:hover{background:#FAFAF8;}

  .arm-fulfill-btn{flex:1;padding:14px 12px;border-radius:4px;font-family:inherit;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;border:2px solid transparent;}
  .arm-fulfill-btn.active{color:#fff;box-shadow:0 8px 28px rgba(0,0,0,.22);}
  .arm-fulfill-btn.inactive{background:transparent;color:#0D0D0D;border-color:#EDE8E0;}
  .arm-fulfill-btn.inactive:hover{border-color:#B8860B;background:rgba(184,134,11,.04);}
  .arm-fulfill-btn.active:hover{opacity:.88;}

  .arm-input{width:100%;padding:11px 12px;border:1px solid rgba(184,134,11,0.18);background:#fff;font-family:inherit;font-size:13px;color:#0D0D0D;outline:none;transition:border-color .15s;border-radius:3px;appearance:none;}
  .arm-input:focus{border-color:#B8860B;}
  .arm-input::placeholder{color:#C4B8A8;}
  .arm-label{display:block;font-family:inherit;font-size:10px;font-weight:700;color:#8A7A6A;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:5px;}

  .arm-badge{display:inline-flex;align-items:center;gap:4px;font-size:9px;font-weight:800;letter-spacing:.1em;padding:2px 8px;border-radius:3px;background:linear-gradient(90deg,#0D0D0D,#B8860B,#CC2200,#FF6B00);background-size:200% 100%;animation:gradSpin 3s ease infinite;color:#fff;}

  .arm-type-pill{padding:5px 14px;border-radius:999px;font-family:inherit;font-size:11px;font-weight:700;letter-spacing:.06em;cursor:pointer;border:2px solid transparent;transition:all .18s;}
  .arm-type-pill.local.on{background:#0D0D0D;color:#fff;border-color:#0D0D0D;}
  .arm-type-pill.regional.on{background:linear-gradient(90deg,#B8860B,#CC2200);color:#fff;border-color:transparent;}
  .arm-type-pill.international.on{background:linear-gradient(90deg,#CC2200,#FF6B00);color:#fff;border-color:transparent;}
  .arm-type-pill.off{background:transparent;color:#8A7A6A;border-color:#EDE8E0;}
  .arm-type-pill.off:hover{border-color:#B8860B;color:#0D0D0D;}

  .arm-save-btn{padding:13px 36px;border:none;border-radius:4px;font-family:inherit;font-size:13px;font-weight:800;letter-spacing:.06em;cursor:pointer;display:flex;align-items:center;gap:8px;transition:all .2s;background:linear-gradient(90deg,#0D0D0D 0%,#B8860B 25%,#CC2200 60%,#FF6B00 100%);background-size:200% 100%;animation:gradSpin 3s ease infinite;color:#fff;box-shadow:0 8px 28px rgba(184,134,11,0.28);}
  .arm-save-btn:disabled{opacity:.35;cursor:not-allowed;animation:none;background:#C4B8A8;box-shadow:none;}
  .arm-save-btn:not(:disabled):hover{transform:translateY(-1px);box-shadow:0 12px 36px rgba(184,134,11,0.38);}

  .arm-add-btn{padding:13px 28px;border:2px solid #0D0D0D;border-radius:4px;font-family:inherit;font-size:13px;font-weight:800;letter-spacing:.06em;cursor:pointer;display:flex;align-items:center;gap:8px;transition:all .2s;background:#0D0D0D;color:#fff;box-shadow:0 6px 20px rgba(0,0,0,.22);}
  .arm-add-btn:hover{background:#1A1A1A;transform:translateY(-1px);}

  .arm-remove-btn{padding:8px 14px;border:1px solid rgba(204,34,0,.25);border-radius:3px;font-family:inherit;font-size:11px;font-weight:700;letter-spacing:.06em;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all .18s;background:rgba(204,34,0,.04);color:#CC2200;}
  .arm-remove-btn:hover{background:rgba(204,34,0,.10);border-color:#CC2200;}
`;

declare global {
  interface Window { google: any; }
}

type FulfilledBy = { isFBA: boolean; isFBS: boolean; isFBP: boolean };
type DeliveryZoneType = "local" | "regional" | "international";

type DeliveryZone = {
  type: DeliveryZoneType;
  country: string;
  city: string;
  neighborhood: string;
  region: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  deliveryFee: number;
  deliveryTime: number;
  fulfilledBy: FulfilledBy;
};

const EMPTY_ZONE = (fulfilledBy: FulfilledBy): DeliveryZone => ({
  type: "local",
  country: "", city: "", neighborhood: "", region: "", zipCode: "",
  latitude: null, longitude: null,
  deliveryFee: 0, deliveryTime: 1,
  fulfilledBy,
});

const ZONE_TYPES: { key: DeliveryZoneType; label: string; desc: string }[] = [
  { key: "local",         label: "Local",         desc: "Within 50 km" },
  { key: "regional",      label: "Regional",       desc: "Same country" },
  { key: "international", label: "International",  desc: "Cross-border" },
];

const FULFILLMENT_OPTIONS = [
  {
    key: "isFBS",
    icon: <Store size={16} />,
    label: "FBS",
    sub: "Fulfilled by Seller",
    gradient: "linear-gradient(135deg,#0D0D0D 0%,#2A2018 100%)",
    shadow: "0 8px 28px rgba(0,0,0,.30)",
  },

  /*
  
    {
    key: "isFBP",
    icon: <Truck size={16} />,
    label: "FBP",
    sub: "Fulfilled by Armerix Partners",
    gradient: "linear-gradient(135deg,#B8860B 0%,#CC2200 60%,#FF6B00 100%)",
    shadow: "0 8px 28px rgba(204,34,0,.32)",
  },
  {
    key: "isFBA",
    icon: <Warehouse size={16} />,
    label: "FBA",
    sub: "Fulfilled by Armerix",
    gradient: "linear-gradient(135deg,#1a6b3c 0%,#15803D 100%)",
    shadow: "0 8px 28px rgba(21,128,61,.30)",
  },
  
  */
 
];

const ZoneTypePill: React.FC<{ value: DeliveryZoneType; onChange: (t: DeliveryZoneType) => void }> = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
    {ZONE_TYPES.map(t => (
      <button key={t.key} className={`arm-type-pill ${t.key} ${value === t.key ? "on" : "off"}`} onClick={() => onChange(t.key)}>
        {t.label}
        <span style={{ fontSize: 9, fontWeight: 400, marginLeft: 4, opacity: .7 }}>{t.desc}</span>
      </button>
    ))}
  </div>
);

const CommerceDeliveryZones: React.FC = () => {
  const dispatch   = useDispatch<any>();
  const userId     = useSelector((s: RootState) => s.auth.user?.userId);
  const { partners } = useSelector((s: RootState) => s.commercePartners);

  const [brandId,   setBrandId]  = useState("");
  const [zones,     setZones]    = useState<DeliveryZone[]>([]);
  const [expanded,  setExpanded] = useState<number | null>(null);
  const [edited,    setEdited]   = useState(false);

  const [brandFulfilledBy, setBrandFulfilledBy] = useState<FulfilledBy>({ isFBA: false, isFBS: true, isFBP: false });

  const inputRefs = useRef<any[]>([]);
  const autoRefs  = useRef<any[]>([]);

  useEffect(() => { if (userId) dispatch(listenToUserCommercePartners(userId)); }, [userId]);

  useEffect(() => {
    if (!brandId) return;
    const partner = partners.find(p => p.brandId === brandId) || null;
    dispatch(setCurrentPartner(partner));
    const fulfillment = partner?.fulfilledBy ?? { isFBA: false, isFBS: true, isFBP: false };
    setBrandFulfilledBy(fulfillment);
    setZones(() => {
      if (partner?.delivery?.length) return partner.delivery.map((z: DeliveryZone) => ({ ...z, fulfilledBy: fulfillment }));
      return [EMPTY_ZONE(fulfillment)];
    });
    setExpanded(0);
    setEdited(false);
  }, [brandId]);

  const toggleFulfillment = (key: keyof FulfilledBy) => {
    setBrandFulfilledBy(prev => {
      // at least one must remain active — prevent all-off state
      const next = { ...prev, [key]: !prev[key] };
      const anyActive = next.isFBS || next.isFBA || next.isFBP;
      if (!anyActive) return prev;
      setZones(z => z.map(zone => ({ ...zone, fulfilledBy: next })));
      setEdited(true);
      return next;
    });
  };

  const updateZone = (i: number, key: keyof DeliveryZone, value: any) => {
    setZones(prev => { const u = [...prev]; u[i] = { ...u[i], [key]: value }; return u; });
    setEdited(true);
  };

  const addZone = () => {
    setZones(prev => {
      const newZones = [...prev, EMPTY_ZONE(brandFulfilledBy)];
      setExpanded(newZones.length - 1);
      return newZones;
    });
    setEdited(true);
  };

  const saveAll = () => {
    if (!brandId) return;
    dispatch(upsertCommercePartner({ brandId, data: { delivery: zones, fulfilledBy: brandFulfilledBy } }));
    setEdited(false);
  };

  /* Google Autocomplete */
  useEffect(() => {
    zones.forEach((_, i) => {
      const input = inputRefs.current[i];
      if (!input || !window.google?.maps?.places || autoRefs.current[i]) return;
      const auto = new window.google.maps.places.Autocomplete(input, { types: ["address"] });
      autoRefs.current[i] = auto;
      auto.addListener("place_changed", () => {
        const place = auto.getPlace();
        const c = place.address_components || [];
        const get = (t: string) => c.find((x: any) => x.types.includes(t))?.long_name || "";
        updateZone(i, "country",      get("country"));
        updateZone(i, "city",         get("locality") || get("administrative_area_level_2"));
        updateZone(i, "neighborhood", get("sublocality") || get("neighborhood"));
        updateZone(i, "region",       get("administrative_area_level_1"));
        updateZone(i, "zipCode",      get("postal_code"));
        updateZone(i, "latitude",     place.geometry?.location?.lat() ?? null);
        updateZone(i, "longitude",    place.geometry?.location?.lng() ?? null);
      });
    });
  }, [zones.length]);

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", minHeight: "100vh", background: T.bg, padding: "32px 24px 80px" }}>
      <style>{CSS}</style>

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ height: 3, width: 32, background: T.gradH, borderRadius: 2 }} />
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: T.muted, textTransform: "uppercase" as const }}>Armerix Commerce</span>
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: T.ink, margin: 0, letterSpacing: -.4 }}>Delivery &amp; Fulfillment</h1>
        <p style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>Configure delivery zones and fulfillment methods for each brand</p>
      </div>

      {/* ── Info Banner ── */}
      <div style={{ background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 6, padding: "18px 20px", marginBottom: 32, position: "relative" as const, overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: T.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite" }} />
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: "50%", background: T.bgMuted, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2 }}>
            <Info size={15} color={T.gold} />
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 10 }}>How delivery zones work</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
              {[
                { tag: "Local", col: T.ink, desc: "Buyer is within 50 km of this zone" },
                { tag: "Regional", col: T.gold, desc: "Buyer is in your country but outside local radius" },
                { tag: "International", col: T.red, desc: "Buyer is outside your country — fees may vary by weight" },
              ].map(r => (
                <div key={r.tag} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: r.col, flexShrink: 0, marginTop: 5 }} />
                  <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, margin: 0 }}><strong style={{ color: T.ink }}>{r.tag}:</strong> {r.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 7, padding: "8px 12px", background: "rgba(204,34,0,.05)", border: "1px solid rgba(204,34,0,.15)", borderRadius: 3 }}>
              <span style={{ fontSize: 14 }}>⚠️</span>
              <p style={{ fontSize: 11, color: T.red, margin: 0 }}>If no matching zone is configured, delivery defaults to <strong>€0 (Free)</strong> — the buyer pays nothing.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Brand Select ── */}
      <div style={{ marginBottom: 32 }}>
        <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".14em", textTransform: "uppercase" as const, color: T.muted, marginBottom: 14 }}>Select Brand</p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" as const }}>
          {partners.map(b => {
            const active = brandId === b.brandId;
            return (
              <div key={b.brandId} onClick={() => setBrandId(b.brandId)}
                style={{ width: 160, borderRadius: 6, padding: 14, cursor: "pointer", background: "#fff", boxShadow: active ? T.shadowGold : T.shadow, border: active ? `2px solid ${T.gold}` : "1px solid #EDE8E0", transform: active ? "translateY(-3px)" : "translateY(0)", transition: "all .2s", position: "relative" as const, overflow: "hidden" }}>
                {active && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: T.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite" }} />}
                <img src={b.image || DefaultLogo} style={{ width: "100%", height: 72, objectFit: "contain", display: "block", marginBottom: 10 }} alt={b.name} />
                <p style={{ textAlign: "center", fontWeight: 700, fontSize: 12, color: T.ink, margin: 0 }}>{b.name}</p>
                {active && <div style={{ marginTop: 6, display: "flex", justifyContent: "center" }}><span className="arm-badge">SELECTED</span></div>}
              </div>
            );
          })}
          {partners.length === 0 && (
            <div style={{ padding: "24px 20px", border: `1px dashed ${T.mutedSoft}`, borderRadius: 6, color: T.muted, fontSize: 12 }}>No brands found — add a brand first.</div>
          )}
        </div>
      </div>

      {/* ── Fulfillment Method ── */}
      {brandId && (
        <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 6, padding: "22px 20px", marginBottom: 28, boxShadow: T.shadow, position: "relative" as const, overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: T.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite" }} />
          <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".14em", textTransform: "uppercase" as const, color: T.muted, marginBottom: 6 }}>Fulfillment Method</p>
          <p style={{ fontSize: 13, color: T.ink, fontWeight: 600, marginBottom: 18 }}>How will orders from this brand be fulfilled?</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" as const }}>
            {FULFILLMENT_OPTIONS.map(opt => {
              const active = brandFulfilledBy[opt.key as keyof FulfilledBy];
              return (
                <button key={opt.key} className={`arm-fulfill-btn${active ? " active" : " inactive"}`}
                  style={active ? { background: opt.gradient, boxShadow: opt.shadow, color: "#fff", border: "2px solid transparent" } : {}}
                  onClick={() => toggleFulfillment(opt.key as keyof FulfilledBy)}>
                  {opt.icon}
                  <div style={{ textAlign: "left" as const }}>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>{opt.label}</div>
                    <div style={{ fontSize: 10, fontWeight: 400, opacity: .85 }}>{opt.sub}</div>
                  </div>
                  <div style={{
                    marginLeft: "auto",
                    width: 18, height: 18,
                    borderRadius: 3,
                    border: `2px solid ${active ? "rgba(255,255,255,.6)" : "#C4B8A8"}`,
                    background: active ? "rgba(255,255,255,.22)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 900,
                    color: active ? "#fff" : "#C4B8A8",
                    transition: "all .15s",
                  }}>
                    {active ? "✓" : "–"}
                  </div>
                </button>
              );
            })}
          </div>
          {/* FBP notice — show if FBP is one of the selected */}
          {brandFulfilledBy.isFBP && (
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(184,134,11,.06)", border: `1px solid ${T.border}`, borderRadius: 3, animation: "slideDown .2s ease" }}>
              <Truck size={13} color={T.gold} />
              <p style={{ fontSize: 11, color: T.gold, fontWeight: 600, margin: 0 }}>FBP — Armerix will coordinate fulfilment through its partner network. Delivery fees are still configurable per zone.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Delivery Zones ── */}
      {brandId && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".14em", textTransform: "uppercase" as const, color: T.muted, margin: 0 }}>Delivery Zones</p>
            <div style={{ flex: 1, height: 1, background: T.border }} />
            <span style={{ fontSize: 11, color: T.muted }}>{zones.length} zone{zones.length !== 1 ? "s" : ""}</span>
          </div>

          {zones.map((z, i) => {
            const open  = expanded === i;
            const label = [z.neighborhood, z.city, z.region, z.country].filter(Boolean).join(", ") || "New zone";
            const typeColor = z.type === "local" ? T.ink : z.type === "regional" ? T.gold : T.red;
            return (
              <div key={i} className="arm-card" style={{ marginBottom: 12 }}>
                {/* Accordion Header */}
                <div className="arm-zone-header" onClick={() => setExpanded(open ? null : i)}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.bgSoft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <MapPin size={14} color={typeColor} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: T.ink, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{label}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: typeColor, textTransform: "uppercase" as const, letterSpacing: ".08em" }}>{z.type}</span>
                      <span style={{ fontSize: 10, color: T.muted }}>· €{z.deliveryFee}</span>
                      <span style={{ fontSize: 10, color: T.muted }}>· {z.deliveryTime}d</span>
                    </div>
                  </div>
                  <div style={{ color: T.muted, flexShrink: 0 }}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
                </div>

                {/* Accordion Body */}
                {open && (
                  <div style={{ padding: "20px 18px", borderTop: `1px solid ${T.border}`, animation: "slideDown .2s ease" }}>

                    {/* Zone type pills */}
                    <div style={{ marginBottom: 20 }}>
                      <label className="arm-label">Zone Type</label>
                      <ZoneTypePill value={z.type} onChange={t => updateZone(i, "type", t)} />
                    </div>

                    {/* Location autocomplete */}
                    <div style={{ marginBottom: 16 }}>
                      <label className="arm-label">Delivery Location</label>
                      <div style={{ position: "relative" as const }}>
                        <input
                          className="arm-input"
                          placeholder="Start typing an address…"
                          defaultValue={[z.neighborhood, z.city, z.region, z.country].filter(Boolean).join(", ")}
                          ref={el => { inputRefs.current[i] = el; }}
                          style={{ paddingLeft: 38 }}
                        />
                        <MapPin size={13} color={T.mutedSoft} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                      </div>
                      {(z.latitude || z.longitude) && (
                        <p style={{ fontSize: 10, color: T.green, marginTop: 4 }}>📍 Coordinates locked: {z.latitude?.toFixed(4)}, {z.longitude?.toFixed(4)}</p>
                      )}
                    </div>

                    {/* Fee + Time row */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                      <div>
                        <label className="arm-label">Delivery Fee (€)</label>
                        <div style={{ position: "relative" as const }}>
                          <input
                            className="arm-input"
                            type="number"
                            disabled={z.fulfilledBy.isFBA}
                            value={z.deliveryFee}
                            onChange={e => updateZone(i, "deliveryFee", +e.target.value)}
                            style={{ paddingLeft: 34, opacity: z.fulfilledBy.isFBA ? .5 : 1 }}
                          />
                          <DollarSign size={12} color={T.mutedSoft} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                        </div>
                        {z.deliveryFee === 0 && <p style={{ fontSize: 10, color: T.green, marginTop: 3 }}>Free delivery</p>}
                      </div>
                      <div>
                        <label className="arm-label">Delivery Time (days)</label>
                        <div style={{ position: "relative" as const }}>
                          <input
                            className="arm-input"
                            type="number"
                            value={z.deliveryTime}
                            onChange={e => updateZone(i, "deliveryTime", Math.max(1, +e.target.value))}
                            style={{ paddingLeft: 34 }}
                          />
                          <Clock size={12} color={T.mutedSoft} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                        </div>
                      </div>
                    </div>

                    {/* Fulfillment override notice */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 3, marginBottom: 16 }}>
                      <Package size={12} color={T.muted} />
                      <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>
                        Fulfilled via:{" "}
                        <strong style={{ color: T.ink }}>
                          {[
                            z.fulfilledBy.isFBS && "FBS — Seller",
                            z.fulfilledBy.isFBP && "FBP — Armerix Partners",
                            z.fulfilledBy.isFBA && "FBA — Armerix",
                          ].filter(Boolean).join(" · ")}
                        </strong>
                      </p>
                    </div>

                    <button className="arm-remove-btn" onClick={() => setZones(prev => prev.filter((_, idx) => idx !== i))}>
                      <Trash2 size={13} /> Remove Zone
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" as const }}>
            <button className="arm-add-btn" onClick={addZone}>
              <Plus size={15} /> Add Zone
            </button>
            <button className="arm-save-btn" onClick={saveAll} disabled={!edited}>
              <Save size={15} /> {edited ? "Save Changes" : "All Saved"}
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!brandId && partners.length > 0 && (
        <div style={{ textAlign: "center" as const, padding: "48px 0", color: T.muted }}>
          <Store size={36} color={T.mutedSoft} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>Select a brand above to configure delivery zones</p>
          <p style={{ fontSize: 12 }}>Each brand can have its own zones, fees, and fulfillment method</p>
        </div>
      )}
    </div>
  );
};

export default CommerceDeliveryZones;